import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Star, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { track } from '../lib/analytics';

const STORAGE_KEY_DISMISSED = 'custel.pilot-nps.dismissed';
const STORAGE_KEY_LAST = 'custel.pilot-nps.lastSubmitted';

interface PilotMetadata {
  pilotStartDate?: string;
}

/**
 * Sprint 9 — pilot anketi banner'ı:
 *   Gün 6 → "pilot-day-6" surveyKey
 *   Gün 9 → "pilot-day-9" surveyKey
 * Sprint 12 — Custel DS token'larına migration (eski indigo paletten arınma).
 */
export function NpsBanner({ pilotStartDate }: PilotMetadata) {
  const { t } = useTranslation('components');
  const [dismissed, setDismissed] = useState(true);
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [surveyKey, setSurveyKey] = useState<string | null>(null);

  useEffect(() => {
    if (!pilotStartDate) return;
    const start = new Date(pilotStartDate).getTime();
    const dayElapsed = Math.floor((Date.now() - start) / (24 * 3600 * 1000));

    let key: string | null = null;
    if (dayElapsed >= 5 && dayElapsed <= 7) key = 'pilot-day-6';
    else if (dayElapsed >= 8 && dayElapsed <= 10) key = 'pilot-day-9';

    if (!key) return;

    const dismissedKeys: string[] = JSON.parse(localStorage.getItem(STORAGE_KEY_DISMISSED) ?? '[]');
    if (dismissedKeys.includes(key)) return;

    setSurveyKey(key);
    setDismissed(false);
  }, [pilotStartDate]);

  const submit = useMutation({
    mutationFn: () =>
      api.post('/pilot/nps', {
        score: score!,
        surveyKey: surveyKey!,
        comment: comment.trim() || undefined,
      }),
    onSuccess: async () => {
      toast.success(t('nps.thanks'));
      const dismissedKeys: string[] = JSON.parse(localStorage.getItem(STORAGE_KEY_DISMISSED) ?? '[]');
      dismissedKeys.push(surveyKey!);
      localStorage.setItem(STORAGE_KEY_DISMISSED, JSON.stringify(dismissedKeys));
      localStorage.setItem(STORAGE_KEY_LAST, new Date().toISOString());
      await track('pilot.nps_submitted', { score, surveyKey });
      setDismissed(true);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : t('common:state.error')),
  });

  const handleDismiss = () => {
    if (surveyKey) {
      const dismissedKeys: string[] = JSON.parse(localStorage.getItem(STORAGE_KEY_DISMISSED) ?? '[]');
      dismissedKeys.push(surveyKey);
      localStorage.setItem(STORAGE_KEY_DISMISSED, JSON.stringify(dismissedKeys));
    }
    setDismissed(true);
  };

  if (dismissed || !surveyKey) return null;

  return (
    <div className="border-b border-custel-lime/40 bg-custel-lime/15 px-6 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="flex items-center gap-1.5 text-sm font-medium text-custel-ink">
            <Star size={14} aria-hidden="true" />
            {t('nps.prompt')}
          </p>
          <div className="mt-2 flex flex-wrap gap-1">
            {Array.from({ length: 11 }, (_, i) => (
              <button
                key={i}
                onClick={() => setScore(i)}
                className={`h-8 w-8 rounded-custel-md text-xs font-semibold transition-colors focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink ${
                  score === i
                    ? 'bg-custel-ink text-custel-krem'
                    : 'bg-custel-paper text-custel-g700 hover:bg-custel-g100 hover:text-custel-ink'
                }`}
                aria-label={t('nps.scoreLabel', { score: i })}
                aria-pressed={score === i}
              >
                {i}
              </button>
            ))}
          </div>
          {score !== null && (
            <>
              <input
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('nps.commentPlaceholder')}
                className="input-base mt-2"
              />
              <button onClick={() => submit.mutate()} disabled={submit.isPending} className="btn-primary btn-sm mt-2">
                {submit.isPending ? t('nps.submitting') : t('nps.submit')}
              </button>
            </>
          )}
        </div>
        <button
          onClick={handleDismiss}
          className="rounded-md p-1 text-custel-g500 transition-colors hover:bg-custel-paper hover:text-custel-ink focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink"
          aria-label={t('common:actions.close')}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
